import { useUsers } from '@api/get-users';
import { useUploadPicture } from '@api/upload-picture';
import { useVerifyBrand } from '@api/verify-brand';
import {
	Button,
	Select,
	Text,
	TextInput,
} from '@components/v2/ui';
import { Switch } from '@components/v2/ui/Switch/Switch';
import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { useCreateBrand } from '@features/offsets/api/create-brand';
import {
	ActionIcon,
	Box,
	Drawer,
	DrawerProps,
	Flex,
	Group,
	SimpleGrid,
	Stack,
} from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';
import useStore from '@store/index';
import { IconPlus } from '@tabler/icons-react';
import {
	convertArrayToLabelValue,
	convertUsersToLabelValue,
} from '@utils/functions';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAllOrganizations } from 'shared/api/get-all-organizations';
import { PictureUpload } from '../PictureUpload/PictureUpload';
import classes from './DrawerCreateBrand2.module.css';

interface BrandFormValues {
	organization: string;
	name: string;
	country: string;
	place: string;
	legalIdentifier: string;
	contact: string | null;
	obfuscateIPFS: boolean;
	logo: string | any;
}

const DrawerCreateBrand = ({
	onSuccess,
	organizationsData,
	contactsData,
	onOpenCreateParent,
	onOpenCreateContact,
	...props
}: DrawerProps & {
	onSuccess?: (values: any) => void;
	organizationsData?: any[];
	contactsData?: any[];
	onOpenCreateParent?: () => void;
	onOpenCreateContact?: () => void;
}) => {
	const { t } = useTranslation();
	/* ******************************** Store States ******************************* */
	const {
		setOpenDrawerOrganization,
		setOpenDrawerContact,
		createdUser,
		setCreatedUser,
		createdOrganization,
		setCreatedOrganization,
		setCreatedBrand,
		setSelectedOrganizationId,
	} = useStore();

	const [pictureData, setPictureData] =
		useState<null | FormData>(null);

	const { data: organizations } = useAllOrganizations();

	const uploadPictureMutation = useUploadPicture({
		mutationConfig: {
			onSuccess(data) {
				form.setFieldValue('logo', data.id);
				form.validate();
				if (form.isValid()) {
					handleSaveData(form.getValues());
				}
			},
		},
	});

	const verifyBrandMutation = useVerifyBrand({
		mutationConfig: {
			onSuccess: () => {},
		},
	});

	const createBrandMutation = useCreateBrand({
		mutationConfig: {
			onSuccess: (data) => {
				if (form.getValues().legalIdentifier !== '') {
					verifyBrandMutation.mutate({
						id: data.id,
						legalIdentifier:
							form.getValues().legalIdentifier,
					});
				}
				setCreatedBrand(data.id.toString());
				form.reset();
				onSuccess?.(data);
				props.onClose();
			},
		},
	});

	const form = useForm<BrandFormValues>({
		initialValues: {
			organization: '',
			name: '',
			country: '',
			place: '',
			legalIdentifier: '',
			contact: null,
			obfuscateIPFS: true,
			logo: '',
		},
		validate: {
			organization: isNotEmpty(
				t('global.organizationRequired')
			),
			name: isNotEmpty(t('global.nameRequired')),
			country: isNotEmpty(t('global.countryRequired')),
			place: isNotEmpty(t('global.placeRequired')),
		},
	});

	useEffect(() => {
		form.setFieldValue('contact', null);
		setSelectedOrganizationId(
			form.values.organization || ''
		);
	}, [form.values.organization]);

	const { data: users } = useUsers(
		{ enabled: !!form.values.organization },
		{ organization: form.values.organization }
	);

	const handleSaveData = (values: BrandFormValues) => {
		form.validate();
		if (form.isValid()) {
			createBrandMutation.mutate({
				body: {
					organization: values.organization,
					name: values.name,
					location: {
						countryCode: values.country,
						place: values.place,
					},

					...(!!values.legalIdentifier
						? { legalIdentifier: values.legalIdentifier }
						: {}),
					...(values.contact
						? { contact: values.contact }
						: {}),
					...(values.obfuscateIPFS
						? { obfuscateIPFS: values.obfuscateIPFS }
						: {}),

					...(values.logo
						? {
								logo: values.logo,
							}
						: {}),
				},
			});
		}
	};
	const handleSubmit = (values: BrandFormValues) => {
		if (form.getValues().logo !== '') {
			uploadPictureMutation.mutate(pictureData);
		} else {
			form.validate();
			if (form.isValid()) {
				createBrandMutation.mutate({
					body: {
						organization: values.organization,
						name: values.name,
						location: {
							countryCode: values.country,
							place: values.place,
						},
						...(!!values.legalIdentifier
							? {
									legalIdentifier: values.legalIdentifier,
								}
							: {}),
						...(values.contact
							? { contact: values.contact }
							: {}),
						...(values.obfuscateIPFS
							? { obfuscateIPFS: values.obfuscateIPFS }
							: {}),
						...(values.logo
							? {
									logo: values.logo,
								}
							: {}),
					},
				});
			}
		}
	};

	useEffect(() => {
		if (createdUser !== '') {
			form.setFieldValue('contact', createdUser);
			setCreatedUser('');
		}
	}, [createdUser]);

	useEffect(() => {
		if (createdOrganization !== '') {
			form.setFieldValue(
				'organization',
				createdOrganization
			);
			setCreatedOrganization('');
		}
	}, [createdOrganization]);

	const handleOnClose = () => {
		form.reset();
		form.clearErrors();
		props.onClose();
	};

	const handleOnUploadProfile = (
		formData: FormData | string | null
	) => {
		if (typeof formData !== 'string') {
			form.setFieldValue('logo', formData);
			setPictureData(formData);
		} else {
			form.setFieldValue('logo', null);
		}
	};

	return (
		<Drawer
			{...props}
			onClose={handleOnClose}
			title={t('drawerCreateBrand.title')}
			position="right"
			size="668px"
			zIndex={98}
			classNames={{
				title: classes.customTitle,
			}}
		>
			<form onSubmit={form.onSubmit(handleSubmit)}>
				<Stack h={'84vh'} justify="space-between" mt="xl">
					<Stack gap="20px">
						<PictureUpload
							onUpload={handleOnUploadProfile}
							src={''}
						/>
						{/* Organisation Group */}
						<Group
							align="flex-start"
							gap="xs"
							wrap="nowrap"
						>
							<Box style={{ flex: 1 }}>
								<Select
									searchable
									label={t('global.organization')}
									withAsterisk
									placeholder={t(
										'filters.placeholders.selectOrganization'
									)}
									data={convertArrayToLabelValue(
										organizations
									)}
									{...form.getInputProps('organization')}
								/>
							</Box>
							<ActionIcon
								variant="outline"
								size="48px"
								radius="xl"
								onClick={() =>
									setOpenDrawerOrganization(true)
								}
								style={{
									border: '1px solid #ced4da',
									marginTop: '25px',
								}}
							>
								<IconPlus size={24} color="#1D3965" />
							</ActionIcon>
						</Group>

						<TextInput
							withAsterisk
							label={t('global.name')}
							placeholder={t('global.name')}
							{...form.getInputProps('name')}
						/>

						<SimpleGrid cols={2} spacing="md">
							<Select
								withAsterisk
								key={form.key('country')}
								label={t('global.country')}
								placeholder={t(
									'placeholders.selectCountry'
								)}
								searchable
								data={EUROPEAN_COUNTRIES.map(
									(country) => ({
										...country,
										label: t(country.label),
									})
								)}
								{...form.getInputProps('country')}
							/>
							<TextInput
								withAsterisk
								label={t('global.place')}
								placeholder={t('global.place')}
								key={form.key('place')}
								{...form.getInputProps('place')}
							/>
						</SimpleGrid>
						<SimpleGrid mt={'xs'} cols={1} spacing="md">
							<Group
								align="center"
								justify="space-between"
							>
								<Text variant="body2" c={'#242424'}>
									{t('global.obfuscateIPFS')}
								</Text>
								<Switch
									size="lg"
									onLabel={t('global.visible')}
									offLabel={t('global.unvisible')}
									{...form.getInputProps(
										'obfuscateIPFS',
										{ type: 'checkbox' }
									)}
								/>
							</Group>
						</SimpleGrid>

						<TextInput
							label={t('global.legalIdentifier')}
							placeholder="Exp : 885 319 871"
							{...form.getInputProps('legalIdentifier')}
						/>

						<Group
							align="flex-start"
							gap="xs"
							wrap="nowrap"
						>
							<Box style={{ flex: 1 }}>
								<Select
									disabled={!form.values.organization}
									label={t('global.selectContact')}
									placeholder={t('global.selectContact')}
									searchable
									data={convertUsersToLabelValue(users)}
									{...form.getInputProps('contact')}
								/>
							</Box>
							<ActionIcon
								disabled={!form.values.organization}
								variant="outline"
								size="48px"
								radius="xl"
								onClick={() => setOpenDrawerContact(true)}
								style={{
									border: '1px solid #ced4da',
									marginTop: '25px',
								}}
							>
								<IconPlus size={24} color="#1D3965" />
							</ActionIcon>
						</Group>
					</Stack>
					<Flex justify="space-between" gap="md" mt="xl">
						<Button
							fullWidth
							variant="outline"
							onClick={handleOnClose}
							style={{
								borderRadius: '24px',
								height: '48px',
							}}
						>
							{t('global.cancel')}
						</Button>
						<Button
							type="submit"
							fullWidth
							variant="primary"
							loading={createBrandMutation.isPending}
							style={{
								borderRadius: '24px',
								height: '48px',
								backgroundColor: '#2B3D63',
							}}
						>
							{t('drawerCreateBrand.btnValidation')}
						</Button>
					</Flex>
				</Stack>
			</form>
		</Drawer>
	);
};

export default DrawerCreateBrand;
